import React, { useEffect, useMemo, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, Polygon } from '@react-google-maps/api';
import { useQuery } from '@apollo/client';

import { Coordinates } from 'apolloClient/types/Neighborhood';
import { Building, BuildingsResponse } from '../../../apolloClient/types';
import {
  calculateCenter,
  getEdgeCoords,
} from '../../../src/utils/locationUtils';
import { InfoWindowData } from '../../../types';
import { BUILDINGS_FOR_MAP } from '../../../apolloClient/queries/neighborhood';
import { flattenStrapiBulkDataItems } from '../../../lib/flattenStrapiBulkDataItems';
import { loadScriptProps } from '../../../constants/gapi/googleMapsConfig';
import { ORIGIN } from '../../../src/constants';
import marker_image from '../../../public/images/map_marker.svg';
import { useJsApiLoader } from '@react-google-maps/api';

type Props = {
  key: number;
  id: number;
  coordinates: Coordinates;
};

const LocationGoogleMap: React.FC<Props> = ({ id, coordinates }) => {
  const { isLoaded, loadError } = useJsApiLoader({ ...loadScriptProps });

  const { data } = useQuery<{ buildings: BuildingsResponse }>(
    BUILDINGS_FOR_MAP,
    { variables: { id } }
  );

  const buildingsByNeighborhoodId = flattenStrapiBulkDataItems(
    data?.buildings.data
  );

  const [path] = useState(coordinates);
  const [center, setCenter] = useState({ lng: 0, lat: 0 });

  const initialInfoWindowData = {
    anchor: undefined,
    buildingName: '',
    isOpen: false,
  };

  const [infoWindowDataHover, setInfoWindowDataHover] =
    useState<InfoWindowData>(initialInfoWindowData);

  useEffect(() => {
    const coords = getEdgeCoords(path);
    const center = calculateCenter(coords);
    setCenter(center);
  }, [path]);

  const mapOptions = useMemo(
    () => ({
      zoom: 14,
      center,
    }),
    [center]
  );

  const polygonOptions = useMemo(
    () => ({
      path: path,
      fillOpacity: 0.2,
      strokeWeight: 2,
      strokeColor: '#666666',
      fillColor: '#999999',
    }),
    [path]
  );

  function closeInfoBlockHover() {
    setInfoWindowDataHover(initialInfoWindowData);
  }

  function openInfoBlockHover(
    anchor: google.maps.Marker | undefined,
    building: Building
  ) {
    setInfoWindowDataHover({ anchor, building, isOpen: true });
  }

  function getBuildingsMarkers(buildings: Building[]) {
    return buildings.reduce<Array<React.JSX.Element>>((acc, building) => {
      let marker: any;
      const { lon, lat, name } = building;
      if (coordinates && lon && lat && window.google && isLoaded) {
        acc.push(
          <Marker
            icon={{
              url: ORIGIN + marker_image.src,
              size: new window.google.maps.Size(14, 14),
            }}
            ref={(ref) => { marker = ref?.marker; }}
            key={name}
            position={{ lng: lon, lat: lat }}
            onMouseOver={() => {
              openInfoBlockHover(marker, building);
            }}
            onMouseOut={closeInfoBlockHover}
          />
        );
      }
      return acc;
    }, []);
  }

  const buildingsMarker = useMemo(
    () => getBuildingsMarkers(buildingsByNeighborhoodId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildingsByNeighborhoodId, isLoaded]
  );

  function setBounds(map: google.maps.Map) {
    const markerBounds = new window.google.maps.LatLngBounds();
    path.forEach((path) => {
      const point = new google.maps.LatLng(path.lat, path.lng);
      markerBounds.extend(point);
    });
    map.fitBounds(markerBounds);
  }

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="h-full App">
      <GoogleMap
        mapContainerClassName="relative w-full h-full"
        options={mapOptions}
        onLoad={setBounds}
      >
        <Polygon options={polygonOptions} />
        {buildingsMarker}
        {infoWindowDataHover.isOpen && (
          <InfoWindow anchor={infoWindowDataHover.anchor}>
            <div style={{ maxWidth: 300, minHeight: 20 }}>
              <div className="flex flex-col">
                <div className="mt-[1px] ml-[9px] mr-[9px] mb-[2px] uppercase font-sohneKraftig">
                  {infoWindowDataHover.building?.name}
                </div>
              </div>
              <style>
                {`
                  .gm-ui-hover-effect {
                    display: none !important;
                  }
                  .gm-style-iw-chr {
                    display: none;
                  }
                  .gm-style-iw-d, .gm-style-iw {
                    min-width: auto !important;
                    overflow: inherit !important;
                  }
                  .gm-style-iw, .gm-style-iw-d {
                    line-height: 22px;
                  }
              `}
              </style>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default LocationGoogleMap;
